import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  IoAlertCircleOutline,
  IoCarSportOutline,
  IoChatbubbleOutline,
  IoCheckmarkCircleOutline,
  IoChevronForward,
  IoMegaphoneOutline,
  IoNotificationsOutline,
  IoRefreshOutline,
  IoSearchOutline,
  IoShieldCheckmarkOutline,
  IoWalletOutline,
} from "react-icons/io5";
import { RootState } from "app/store";
import { useTabActive } from "hook/useTabActive";
import { SideBarTabs } from "constants/tabs";
import Menu from "components/common/Menu";
import { CommonStyles } from "styles/CommonStyles";
import useNotifications from "hook/useNotifications";
import { NotificationItemType } from "types/loadedData";
import { getMonthDate, getTime } from "functions/text";
import { BroadcastNotification } from "api/services/notifications";
import { dashboardConfirm } from "i18n/dashboardLanguage";
import { GetCars } from "api/services/car";
import { CarType } from "types/loadedData";

type InboxFilter = "all" | "unread" | "activity" | "system";

const notificationIcon = (type: NotificationItemType["type"]) => {
  switch (type) {
    case "message":
      return IoChatbubbleOutline;
    case "car":
      return IoCarSportOutline;
    case "moneyRequest":
      return IoWalletOutline;
    case "report":
      return IoShieldCheckmarkOutline;
    case "marketing":
      return IoMegaphoneOutline;
    default:
      return IoNotificationsOutline;
  }
};

function NotificationsPage() {
  const { user } = useSelector((state: RootState) => state.user);
  const {
    notifications,
    unreadCount,
    loaded,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    totalCount,
    markNotificationReadById,
    markAllAsRead,
    openNotificationTarget,
    refreshNotifications,
    loadMoreNotifications,
  } = useNotifications({ subscribe: false });
  const isAdmin = user?.role === "Admin";
  const [showComposer, setShowComposer] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [showAmharic, setShowAmharic] = useState(false);
  const [titleAmharic, setTitleAmharic] = useState("");
  const [bodyAmharic, setBodyAmharic] = useState("");
  const [broadcastType, setBroadcastType] = useState<"system" | "marketing">(
    "system"
  );
  const [carSearch, setCarSearch] = useState("");
  const [carResults, setCarResults] = useState<CarType[]>([]);
  const [selectedCar, setSelectedCar] = useState<CarType | null>(null);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState<InboxFilter>("all");
  const [search, setSearch] = useState("");
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useTabActive(SideBarTabs.Notifications.name);

  useEffect(() => {
    if (broadcastType !== "marketing" || carSearch.trim().length < 2) {
      setCarResults([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await GetCars({ search: carSearch.trim(), status: "Active", limit: 8 });
        setCarResults(response.cars || []);
      } catch {
        setCarResults([]);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [broadcastType, carSearch]);

  const selectPromotionCar = (car: CarType) => {
    setSelectedCar(car);
    setCarResults([]);
    const make = typeof car.make === "object" ? car.make?.name : car.make;
    const carTitle = [car.year, make, car.model].filter(Boolean).join(" ");
    if (!title.trim()) setTitle(carTitle);
    if (!body.trim()) setBody(`${carTitle} is available on BossCar for ${Number(car.price || 0).toLocaleString("en-US")} Birr. Tap to view the listing.`);
  };

  const visibleNotifications = useMemo(() => {
    const query = search.trim().toLowerCase();
    return notifications.filter((notification) => {
      if (filter === "unread" && notification.readAt) return false;
      if (
        filter === "system" &&
        !["system", "marketing", "report"].includes(notification.type)
      ) {
        return false;
      }
      if (
        filter === "activity" &&
        ["system", "marketing", "report"].includes(notification.type)
      ) {
        return false;
      }
      if (!query) return true;
      return [notification.title, notification.body, notification.kind]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [filter, notifications, search]);

  const handleNotificationPress = (notification: NotificationItemType) => {
    openNotificationTarget(notification);
    void markNotificationReadById(notification).catch(() => undefined);
  };

  const handleBroadcastSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isAdmin || sending) return;

    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || !trimmedBody) {
      setFeedback({ type: "error", text: "Add both a title and a message." });
      return;
    }
    const trimmedTitleAmharic = titleAmharic.trim();
    const trimmedBodyAmharic = bodyAmharic.trim();
    if (showAmharic && (!trimmedTitleAmharic || !trimmedBodyAmharic)) {
      setFeedback({
        type: "error",
        text: "Add both the Amharic title and message, or remove the Amharic version.",
      });
      return;
    }

    const audienceText =
      broadcastType === "system"
        ? "all active accounts"
        : "users who opted into offers";
    if (!dashboardConfirm(`Send this notification to ${audienceText}?`)) return;

    try {
      setFeedback(null);
      const response = await BroadcastNotification({
        setLoading: setSending,
        recipientScope: "all",
        title: trimmedTitle,
        body: trimmedBody,
        type: broadcastType,
        titleAmharic: showAmharic ? trimmedTitleAmharic : undefined,
        bodyAmharic: showAmharic ? trimmedBodyAmharic : undefined,
        carId: selectedCar ? String(selectedCar._id) : undefined,
      });
      if (response.status !== 201) {
        throw new Error(response.message || "Notification could not be sent");
      }

      const recipientCount = response.recipientCount || 0;
      setFeedback({
        type: "success",
        text: `Sent successfully to ${recipientCount} account${
          recipientCount === 1 ? "" : "s"
        }.`,
      });
      setTitle("");
      setBody("");
      setTitleAmharic("");
      setBodyAmharic("");
      setShowAmharic(false);
      setSelectedCar(null);
      setCarSearch("");
      await refreshNotifications();
    } catch (submitError: any) {
      setFeedback({
        type: "error",
        text: submitError?.message || "Notification could not be sent.",
      });
    }
  };

  const filters: Array<{ key: InboxFilter; label: string }> = [
    { key: "all", label: "All" },
    { key: "unread", label: "Unread" },
    { key: "activity", label: "Activity" },
    { key: "system", label: "System" },
  ];

  return (
    <div className={CommonStyles.pageWrapper}>
      <Menu />
      <main className={ClassName.container}>
        <section className={ClassName.pageHeader}>
          <div>
            <h1 className={ClassName.pageTitle}>Notifications</h1>
            <p className={ClassName.pageSubtitle}>
              {unreadCount > 0
                ? `${unreadCount} unread · ${totalCount} total`
                : "You are all caught up"}
            </p>
          </div>
          <div className={ClassName.headerActions}>
            <button
              type="button"
              className={ClassName.secondaryButton}
              onClick={() => refreshNotifications()}
              disabled={isLoading}
            >
              <IoRefreshOutline size={18} className={isLoading ? "animate-spin" : ""} />
              Refresh
            </button>
            {unreadCount > 0 ? (
              <button
                type="button"
                className={ClassName.secondaryButton}
                onClick={() => markAllAsRead()}
              >
                <IoCheckmarkCircleOutline size={18} />
                Mark all read
              </button>
            ) : null}
            {isAdmin ? (
              <button
                type="button"
                className={ClassName.primaryButton}
                onClick={() => setShowComposer((current) => !current)}
              >
                <IoMegaphoneOutline size={18} />
                {showComposer ? "Close composer" : "Send announcement"}
              </button>
            ) : null}
          </div>
        </section>

        {isAdmin && showComposer ? (
          <form className={ClassName.composer} onSubmit={handleBroadcastSubmit}>
            <div className={ClassName.composerIntro}>
              <div>
                <h2 className={ClassName.sectionTitle}>Send an announcement</h2>
                <p className={ClassName.sectionSubtitle}>
                  Use system alerts only for important service or account information.
                </p>
              </div>
              <div className={ClassName.typeSelector}>
                <button
                  type="button"
                  className={`${ClassName.typeButton} ${
                    broadcastType === "system" ? ClassName.activeTypeButton : ""
                  }`}
                  onClick={() => setBroadcastType("system")}
                >
                  Important update
                </button>
                <button
                  type="button"
                  className={`${ClassName.typeButton} ${
                    broadcastType === "marketing" ? ClassName.activeTypeButton : ""
                  }`}
                  onClick={() => setBroadcastType("marketing")}
                >
                  Offer / promotion
                </button>
              </div>
            </div>

            <div className={ClassName.composerGrid}>
              <div className={ClassName.fields}>
                {broadcastType === "marketing" ? (
                  <div className={ClassName.carPicker}>
                    <label className={ClassName.label}>Attach a posted car <span>optional</span></label>
                    {selectedCar ? (
                      <div className={ClassName.selectedCar}>
                        <div className="min-w-0 flex-1">
                          <strong className="block truncate text-sm">{[selectedCar.year, typeof selectedCar.make === "object" ? selectedCar.make?.name : selectedCar.make, selectedCar.model].filter(Boolean).join(" ")}</strong>
                          <span className="text-xs text-slate-500">{Number(selectedCar.price || 0).toLocaleString("en-US")} Birr</span>
                        </div>
                        <button type="button" onClick={() => setSelectedCar(null)}>Remove</button>
                      </div>
                    ) : (
                      <>
                        <div className={ClassName.carSearch}><IoSearchOutline size={18} /><input value={carSearch} onChange={(event) => setCarSearch(event.target.value)} placeholder="Search an active car by make or model" /></div>
                        {carResults.length ? <div className={ClassName.carResults}>{carResults.map((car) => <button key={String(car._id)} type="button" onClick={() => selectPromotionCar(car)}>{[car.year, typeof car.make === "object" ? car.make?.name : car.make, car.model].filter(Boolean).join(" ")} <span>{Number(car.price || 0).toLocaleString("en-US")} Birr</span></button>)}</div> : null}
                      </>
                    )}
                  </div>
                ) : null}
                <label className={ClassName.label}>
                  Title <span>{title.length}/120</span>
                </label>
                <input
                  className={ClassName.input}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Clear, short title"
                  maxLength={120}
                />
                <label className={ClassName.label}>
                  Message <span>{body.length}/500</span>
                </label>
                <textarea
                  className={ClassName.textarea}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder="Tell users what happened and what they should do"
                  rows={5}
                  maxLength={500}
                />
                <button
                  type="button"
                  className={ClassName.translationButton}
                  onClick={() => setShowAmharic((current) => !current)}
                >
                  {showAmharic ? "Remove Amharic version" : "+ Add Amharic version"}
                </button>
                {showAmharic ? (
                  <div className={ClassName.translationFields}>
                    <label className={ClassName.label}>
                      Amharic title <span>{titleAmharic.length}/120</span>
                    </label>
                    <input
                      className={ClassName.input}
                      value={titleAmharic}
                      onChange={(event) => setTitleAmharic(event.target.value)}
                      placeholder="የማሳወቂያ ርዕስ"
                      maxLength={120}
                    />
                    <label className={ClassName.label}>
                      Amharic message <span>{bodyAmharic.length}/500</span>
                    </label>
                    <textarea
                      className={ClassName.textarea}
                      value={bodyAmharic}
                      onChange={(event) => setBodyAmharic(event.target.value)}
                      placeholder="ለተጠቃሚዎች የሚታየው መልዕክት"
                      rows={4}
                      maxLength={500}
                    />
                  </div>
                ) : null}
              </div>
              <div className={ClassName.preview}>
                <span className={ClassName.previewLabel}>Preview</span>
                <div className={ClassName.previewCard}>
                  <div className={ClassName.previewIcon}>
                    <IoNotificationsOutline size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className={ClassName.previewTitle}>
                      {title.trim() || "Announcement title"}
                    </p>
                    <p className={ClassName.previewBody}>
                      {body.trim() || "Your message will appear here."}
                    </p>
                  </div>
                </div>
                <p className={ClassName.audienceNote}>
                  {broadcastType === "system"
                    ? "Audience: every active BossCar account"
                    : "Audience: users who enabled offers and promotions"}
                </p>
              </div>
            </div>

            {feedback ? (
              <div
                className={`${ClassName.feedback} ${
                  feedback.type === "success"
                    ? ClassName.feedbackSuccess
                    : ClassName.feedbackError
                }`}
              >
                {feedback.type === "error" ? <IoAlertCircleOutline size={18} /> : null}
                {feedback.text}
              </div>
            ) : null}
            <div className={ClassName.composerActions}>
              <button
                type="submit"
                className={ClassName.primaryButton}
                disabled={sending}
              >
                {sending ? "Sending..." : "Review and send"}
              </button>
            </div>
          </form>
        ) : null}

        <section className={ClassName.inbox}>
          <div className={ClassName.inboxToolbar}>
            <div className={ClassName.filters}>
              {filters.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={`${ClassName.filterButton} ${
                    filter === item.key ? ClassName.activeFilter : ""
                  }`}
                  onClick={() => setFilter(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <label className={ClassName.searchBox}>
              <IoSearchOutline size={18} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search notifications"
              />
            </label>
          </div>

          <div className={ClassName.list}>
            {visibleNotifications.map((notification) => {
              const Icon = notificationIcon(notification.type);
              const isUnread = !notification.readAt;
              const hasTarget = Boolean(notification.data?.screen);
              return (
                <button
                  type="button"
                  key={notification._id}
                  className={`${ClassName.card} ${
                    isUnread ? ClassName.unreadCard : ClassName.readCard
                  }`}
                  onClick={() => handleNotificationPress(notification)}
                >
                  <div className={`${ClassName.icon} ${isUnread ? ClassName.unreadIcon : ""}`}>
                    <Icon size={20} />
                  </div>
                  <div className={ClassName.cardContent}>
                    <div className={ClassName.cardTitleRow}>
                      <span className={ClassName.cardTitle}>{notification.title}</span>
                      {isUnread ? <span className={ClassName.dot} /> : null}
                    </div>
                    <span className={ClassName.cardBody}>{notification.body}</span>
                    <span className={ClassName.cardMeta}>
                      {getMonthDate(notification.createdAt)} · {getTime(notification.createdAt)}
                    </span>
                  </div>
                  {hasTarget ? <IoChevronForward size={18} className="text-slate-400" /> : null}
                </button>
              );
            })}

            {isLoading && !loaded ? (
              <div className={ClassName.emptyState}>Loading notifications...</div>
            ) : null}
            {error && visibleNotifications.length === 0 ? (
              <div className={ClassName.emptyState}>
                <IoAlertCircleOutline size={28} />
                Notifications could not be loaded.
                <button type="button" className={ClassName.retryLink} onClick={() => refreshNotifications()}>
                  Try again
                </button>
              </div>
            ) : null}
            {!isLoading && !error && visibleNotifications.length === 0 ? (
              <div className={ClassName.emptyState}>
                <IoCheckmarkCircleOutline size={34} className="text-emerald-600" />
                {filter === "unread" ? "No unread notifications." : "No notifications found."}
              </div>
            ) : null}
            {hasMore ? (
              <button
                type="button"
                className={ClassName.loadMoreButton}
                onClick={() => loadMoreNotifications()}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? "Loading..." : "Load more"}
              </button>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}

export default NotificationsPage;

const ClassName = {
  container: "flex min-w-0 flex-1 flex-col gap-5 text-primaryText",
  pageHeader:
    "flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between",
  pageTitle: "text-2xl font-bold tracking-tight text-slate-950",
  pageSubtitle: "mt-1 text-sm text-secondaryText",
  headerActions: "flex flex-wrap items-center gap-2",
  primaryButton:
    "inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
  secondaryButton:
    "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50",
  composer: "flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm",
  composerIntro: "flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between",
  sectionTitle: "text-lg font-bold text-slate-950",
  sectionSubtitle: "mt-1 text-sm text-secondaryText",
  typeSelector: "inline-flex w-fit rounded-xl bg-slate-100 p-1",
  typeButton: "rounded-lg px-3 py-2 text-xs font-semibold text-slate-500",
  activeTypeButton: "bg-white text-slate-950 shadow-sm",
  composerGrid: "grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.7fr)]",
  fields: "flex min-w-0 flex-col gap-2",
  carPicker: "relative mb-2 flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3",
  carSearch: "flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-slate-400 [&>input]:min-w-0 [&>input]:flex-1 [&>input]:border-0 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:outline-none",
  carResults: "absolute left-3 right-3 top-[92px] z-20 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg [&>button]:flex [&>button]:w-full [&>button]:items-center [&>button]:justify-between [&>button]:gap-3 [&>button]:rounded-lg [&>button]:px-3 [&>button]:py-2.5 [&>button]:text-left [&>button]:text-sm hover:[&>button]:bg-slate-50 [&>button>span]:text-xs [&>button>span]:text-slate-500",
  selectedCar: "flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 [&>button]:text-xs [&>button]:font-semibold [&>button]:text-slate-500",
  label: "mt-1 flex justify-between text-xs font-semibold text-slate-700 [&>span]:font-normal [&>span]:text-slate-400",
  input: "h-11 rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-primary",
  textarea: "resize-none rounded-xl border border-slate-200 px-3 py-3 text-sm outline-none focus:border-primary",
  translationButton:
    "mt-2 w-fit rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50",
  translationFields:
    "mt-1 flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3",
  preview: "rounded-2xl bg-slate-50 p-4",
  previewLabel: "text-xs font-semibold uppercase tracking-wider text-slate-400",
  previewCard: "mt-3 flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm",
  previewIcon: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-primary",
  previewTitle: "truncate text-sm font-semibold text-slate-950",
  previewBody: "mt-1 text-sm leading-5 text-slate-500",
  audienceNote: "mt-3 text-xs leading-5 text-slate-500",
  feedback: "flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium",
  feedbackSuccess: "border-emerald-200 bg-emerald-50 text-emerald-700",
  feedbackError: "border-red-200 bg-red-50 text-red-700",
  composerActions: "flex justify-end",
  inbox: "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm",
  inboxToolbar: "flex flex-col gap-3 border-b border-slate-200 p-4 lg:flex-row lg:items-center lg:justify-between",
  filters: "flex flex-wrap gap-2",
  filterButton: "h-9 rounded-full border border-slate-200 px-4 text-xs font-semibold text-slate-500 hover:bg-slate-50",
  activeFilter: "border-slate-900 bg-slate-900 text-white hover:bg-slate-900",
  searchBox: "flex h-10 w-full items-center gap-2 rounded-xl border border-slate-200 px-3 text-slate-400 lg:w-72 [&>input]:min-w-0 [&>input]:flex-1 [&>input]:border-0 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:outline-none",
  list: "flex max-h-[calc(100vh-250px)] min-h-72 flex-col overflow-y-auto p-3",
  card: "flex w-full items-start gap-3 rounded-xl border border-transparent p-3 text-left transition-colors hover:bg-slate-50",
  unreadCard: "bg-red-50/50",
  readCard: "bg-white",
  icon: "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500",
  unreadIcon: "bg-red-100 text-primary",
  cardContent: "min-w-0 flex-1",
  cardTitleRow: "flex items-center gap-2",
  cardTitle: "truncate text-sm font-semibold text-slate-950",
  cardBody: "mt-1 block text-sm leading-5 text-slate-500",
  cardMeta: "mt-2 block text-xs text-slate-400",
  dot: "h-2 w-2 shrink-0 rounded-full bg-primary",
  emptyState: "flex min-h-64 flex-col items-center justify-center gap-2 text-sm text-slate-500",
  retryLink: "font-semibold text-primary",
  loadMoreButton: "mx-auto my-3 h-10 rounded-xl px-5 text-sm font-semibold text-primary hover:bg-red-50 disabled:opacity-50",
};
